import { logger } from '@automata/utils';
import type { Schemas, WorkflowConfigInput } from '@automata/wfb-api';

import { err, ok } from './utils';
import type { TimeConstraintInstancesById } from '../../features/graph/graphSlice';
import type {
  TaskLabwareCorrespondenceById,
  TimeConstraintErrorsById,
  TimeConstraintInstance,
} from '../../types';
import {
  INVALID_TIME_CONSTRAINT,
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../shared/types';

const _buildTimeConstraintInput = (
  base: Pick<
    TimeConstraintInstance,
    'source' | 'sourceType' | 'target' | 'targetType'
  >,
  id: string,
  minDuration: number | null,
  maxDuration: number | null,
  enableDetailedCoverageCalculation: boolean | undefined,
  autoIgnoreIfBroken: boolean | undefined
): Schemas.TimeConstraintInput | null => {
  if (minDuration === null && maxDuration === null) return null;
  const tcType: Schemas.TimeConstraintType = `${base.sourceType}_to_${base.targetType}`;
  return {
    id,
    type: tcType,
    start_task_id: base.source,
    end_task_id: base.target,
    min_duration: minDuration,
    max_duration: maxDuration,
    enable_detailed_coverage_calculation:
      enableDetailedCoverageCalculation ?? true,
    auto_ignore_if_broken: autoIgnoreIfBroken ?? false,
  };
};

export const getJsonTimeConstraintsStrict = (
  timeConstraintInstances: TimeConstraintInstancesById
): MaybeWorkflowSerializationErrors<
  WorkflowConfigInput['workflow']['time_constraints']
> => {
  // Desired behavior: structural time-constraint connections without min/max
  // should NOT appear in workflow.json until the user saves a rule in the modal.
  // Therefore, we silently skip instances where both durations are null.
  const data: Schemas.TimeConstraintInput[] = [];

  for (const instance of Object.values(timeConstraintInstances)) {
    const { id, source, sourceType, target, targetType, hardConstraints } =
      instance;

    if (hardConstraints && hardConstraints.length > 0) {
      hardConstraints.forEach((hc, idx) => {
        const hcId = `${id}_${idx + 1}`;
        const input = _buildTimeConstraintInput(
          { source, sourceType, target, targetType },
          hcId,
          hc.minDuration,
          hc.maxDuration,
          hc.enableDetailedCoverageCalculation,
          hc.autoIgnoreIfBroken
        );
        if (input) data.push(input);
      });
      continue;
    }

    const legacyInput = _buildTimeConstraintInput(
      { source, sourceType, target, targetType },
      id,
      instance.minDuration,
      instance.maxDuration,
      instance.enableDetailedCoverageCalculation,
      instance.autoIgnoreIfBroken
    );
    if (legacyInput) data.push(legacyInput);
  }

  return ok(data);
};

//** Filter out invalid time constraint instances */
export const getJsonTimeConstraintsPreview = (
  timeConstraintInstancesById: TimeConstraintInstancesById,
  labwareCorrespondenceById: TaskLabwareCorrespondenceById,
  timeConstraintErrorsById: TimeConstraintErrorsById
): MaybeWorkflowSerializationErrors<
  WorkflowConfigInput['workflow']['time_constraints']
> => {
  const data: Schemas.TimeConstraintInput[] = [];

  for (const instance of Object.values(timeConstraintInstancesById)) {
    const { id, source, sourceType, target, targetType, hardConstraints } =
      instance;
    const labwareCorrespondence = labwareCorrespondenceById[id];
    if (!labwareCorrespondence)
      logger.error('No labware correspondence found for id:', id);

    const noLabwareIsAssigned = labwareCorrespondence === 'NO_LABWARE_ASSIGNED';
    if (noLabwareIsAssigned) continue;

    if (hardConstraints && hardConstraints.length > 0) {
      hardConstraints.forEach((hc, idx) => {
        const hcId = `${id}_${idx + 1}`;
        const input = _buildTimeConstraintInput(
          { source, sourceType, target, targetType },
          hcId,
          hc.minDuration,
          hc.maxDuration,
          hc.enableDetailedCoverageCalculation,
          hc.autoIgnoreIfBroken
        );
        if (input) data.push(input);
      });
      continue;
    }

    const legacyInput = _buildTimeConstraintInput(
      { source, sourceType, target, targetType },
      id,
      instance.minDuration,
      instance.maxDuration,
      instance.enableDetailedCoverageCalculation,
      instance.autoIgnoreIfBroken
    );
    if (legacyInput) data.push(legacyInput);
  }

  // TODO(IL, 2024-10-11): just use filter not flatMap when we upgrade TypeScript
  const errors: WorkflowSerializationError[] = Object.values(
    timeConstraintErrorsById
  ).flatMap((e) =>
    e === undefined
      ? []
      : { errorType: 'timeConstraints', message: INVALID_TIME_CONSTRAINT }
  );

  return errors.length > 0 ? err(errors) : ok(data);
};

export const getJsonTimeConstraintsForSave = (
  timeConstraintInstancesById: TimeConstraintInstancesById
): WorkflowConfigInput['workflow']['time_constraints'] => {
  const data: WorkflowConfigInput['workflow']['time_constraints'] = [];

  for (const instance of Object.values(timeConstraintInstancesById)) {
    const { id, source, sourceType, target, targetType, hardConstraints } =
      instance;

    if (hardConstraints && hardConstraints.length > 0) {
      hardConstraints.forEach((hc, idx) => {
        const hcId = `${id}_${idx + 1}`;
        const input = _buildTimeConstraintInput(
          { source, sourceType, target, targetType },
          hcId,
          hc.minDuration,
          hc.maxDuration,
          hc.enableDetailedCoverageCalculation,
          hc.autoIgnoreIfBroken
        );
        if (input) data.push(input);
      });
      continue;
    }

    const legacyInput = _buildTimeConstraintInput(
      { source, sourceType, target, targetType },
      id,
      instance.minDuration,
      instance.maxDuration,
      instance.enableDetailedCoverageCalculation,
      instance.autoIgnoreIfBroken
    );
    if (legacyInput) data.push(legacyInput);
  }

  return data;
};
